/**
 * Admin Competitions Management Page
 * TBMuktBharat 2026 - Government Grade UI
 */

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Trophy, Plus, Edit, Trash2, Eye, Users, Calendar, Clock,
  Search, Filter, ChevronDown, ChevronUp, Award, FileText,
  Image, PenTool, MessageSquare, CheckCircle, XCircle, Send,
  Download, BarChart2, FileSpreadsheet
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import competitionService, { Competition, CompetitionStats, CompetitionType, CompetitionStatus } from '@/services/competitionService'
import { Button, Card, Badge, EmptyState, Skeleton } from '@/components/ui/premium-components'
import { DataTable, Modal, Tabs, InputField, Select, StatsCard } from '@/components/ui/data-display'
import { useAuthStore } from '@/stores/authStore'
import GovLayout from '@/components/layout/GovLayout'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'

// Competition type configurations
const COMPETITION_TYPES = [
  { value: 'POSTER', label: 'Poster Competition', icon: Image, color: 'bg-purple-500' },
  { value: 'QUIZ', label: 'Quiz Competition', icon: FileText, color: 'bg-blue-500' },
  { value: 'ESSAY', label: 'Essay Writing', icon: PenTool, color: 'bg-green-500' },
  { value: 'DRAWING', label: 'Drawing Competition', icon: Image, color: 'bg-orange-500' },
  { value: 'SLOGAN', label: 'Slogan Writing', icon: MessageSquare, color: 'bg-pink-500' },
]

const COMPETITION_STATUS = [
  { value: 'DRAFT', label: 'Draft', color: 'bg-neutral-500' },
  { value: 'ACTIVE', label: 'Active', color: 'bg-green-500' },
  { value: 'CLOSED', label: 'Closed', color: 'bg-red-500' },
  { value: 'ARCHIVED', label: 'Archived', color: 'bg-neutral-400' },
]

interface CompetitionFormData {
  title: string
  description: string
  type: CompetitionType
  startDate: string
  endDate: string
  maxParticipants: number
  prizeDescription: string
  rules: string
  guidelines: string
}

const initialFormData: CompetitionFormData = {
  title: '',
  description: '',
  type: 'POSTER',
  startDate: '',
  endDate: '',
  maxParticipants: 100,
  prizeDescription: '',
  rules: '',
  guidelines: '',
}

const competitionExportColumns: ExportColumn<Competition>[] = [
  { header: 'ID', key: 'id' },
  { header: 'Title', key: 'title' },
  { header: 'Type', key: 'typeDisplayName', format: (v, row) => (v ? String(v) : String((row as Competition).type || '')) },
  { header: 'Status', key: 'statusDisplayName', format: (v, row) => (v ? String(v) : String((row as Competition).status || '')) },
  { header: 'Featured', key: 'isFeatured', format: (v) => (v ? 'Yes' : 'No') },
  { header: 'Start Date', key: 'startDate', format: (v) => (v ? new Date(v as string).toLocaleDateString() : '') },
  { header: 'End Date', key: 'endDate', format: (v) => (v ? new Date(v as string).toLocaleDateString() : '') },
  { header: 'Max Participants', key: 'maxParticipants', format: (v) => (v != null ? String(v) : '∞') },
  { header: 'Current Participants', key: 'currentParticipants', format: (v) => (v != null ? String(v) : '0') },
  { header: 'Description', key: 'description', format: (v) => (v ? String(v).replace(/\s+/g, ' ').slice(0, 300) : '') },
  { header: 'Created', key: 'createdAt', format: (v) => (v ? new Date(v as string).toLocaleString() : '') },
]

export const AdminCompetitionsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  // State
  const [competitions, setCompetitions] = useState<Competition[]>([])
  const [stats, setStats] = useState<CompetitionStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [activeTab, setActiveTab] = useState('all')

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null)
  const [formData, setFormData] = useState<CompetitionFormData>(initialFormData)
  const [submitting, setSubmitting] = useState(false)

  // Fetch competitions
  useEffect(() => {
    fetchCompetitions()
    fetchStats()
  }, [filterType, filterStatus])

  const fetchCompetitions = async () => {
    try {
      setLoading(true)
      const common = {
        type: filterType ? (filterType as CompetitionType) : undefined,
        search: searchQuery || undefined,
        size: 100,
      }

      let list: Competition[] = []

      if (filterStatus) {
        // A specific status is chosen in the dropdown -> fetch just that status.
        const response = await competitionService.getCompetitions({
          ...common,
          status: filterStatus as CompetitionStatus,
        })
        list = response.content || []
      } else {
        // "All Status" / default. The backend defaults an omitted status param to
        // ACTIVE, which hides DRAFT + CLOSED competitions (so newly created ones
        // never appear). Fetch each status and merge so the admin sees everything.
        const statuses: CompetitionStatus[] = ['DRAFT', 'ACTIVE', 'CLOSED']
        const results = await Promise.all(
          statuses.map((s) =>
            competitionService.getCompetitions({ ...common, status: s }).catch(() => null)
          )
        )
        const seen = new Set<number>()
        for (const r of results) {
          for (const c of r?.content || []) {
            if (!seen.has(c.id)) {
              seen.add(c.id)
              list.push(c)
            }
          }
        }
        // newest first (createdAt is ISO string)
        list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
      }

      setCompetitions(list)
    } catch (error) {
      console.error('Failed to fetch competitions:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await competitionService.getStats()
      setStats(response)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchCompetitions()
  }

  const handleCreate = async () => {
    try {
      setSubmitting(true)
      await competitionService.createCompetition(formData)
      setIsCreateModalOpen(false)
      setFormData(initialFormData)
      fetchCompetitions()
      fetchStats()
    } catch (error) {
      console.error('Failed to create competition:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdate = async () => {
    if (!selectedCompetition) return
    try {
      setSubmitting(true)
      await competitionService.updateCompetition(selectedCompetition.id, formData)
      setIsEditModalOpen(false)
      setSelectedCompetition(null)
      setFormData(initialFormData)
      fetchCompetitions()
    } catch (error) {
      console.error('Failed to update competition:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedCompetition) return
    try {
      setSubmitting(true)
      await competitionService.deleteCompetition(selectedCompetition.id)
      setIsDeleteModalOpen(false)
      setSelectedCompetition(null)
      fetchCompetitions()
      fetchStats()
    } catch (error) {
      console.error('Failed to delete competition:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handlePublish = async (competition: Competition) => {
    try {
      await competitionService.publishCompetition(competition.id)
      fetchCompetitions()
      fetchStats()
    } catch (error) {
      console.error('Failed to publish competition:', error)
    }
  }

  const handleClose = async (competition: Competition) => {
    try {
      await competitionService.closeCompetition(competition.id)
      fetchCompetitions()
      fetchStats()
    } catch (error) {
      console.error('Failed to close competition:', error)
    }
  }

  const handleExportSubmissions = async (competition: Competition) => {
    try {
      const blob = await competitionService.exportSubmissionsCsv(competition.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `submissions_competition_${competition.id}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to export submissions:', error)
    }
  }

  const openEditModal = (competition: Competition) => {
    setSelectedCompetition(competition)
    setFormData({
      title: competition.title,
      description: competition.description || '',
      type: competition.type,
      startDate: competition.startDate?.split('T')[0] || '',
      endDate: competition.endDate?.split('T')[0] || '',
      maxParticipants: competition.maxParticipants || 100,
      prizeDescription: competition.prizeDescription || '',
      rules: competition.rules || '',
      guidelines: competition.guidelines || '',
    })
    setIsEditModalOpen(true)
  }

  const getStatusColor = (status: string) => {
    const statusConfig = COMPETITION_STATUS.find(s => s.value === status)
    return statusConfig?.color || 'bg-neutral-500'
  }

  const getTypeIcon = (type: string) => {
    const typeConfig = COMPETITION_TYPES.find(t => t.value === type)
    return typeConfig?.icon || FileText
  }

  const getTypeColor = (type: string) => {
    const typeConfig = COMPETITION_TYPES.find(t => t.value === type)
    return typeConfig?.color || 'bg-neutral-500'
  }

  // Filter competitions by tab
  const filteredCompetitions = competitions.filter(comp => {
    if (activeTab === 'all') return true
    if (activeTab === 'draft') return comp.status === 'DRAFT'
    if (activeTab === 'active') return comp.status === 'ACTIVE'
    if (activeTab === 'closed') return comp.status === 'CLOSED'
    return true
  })

  // Stats cards
  const statsCards = [
    {
      icon: <Trophy className="w-6 h-6 text-primary-600" />,
      label: 'Total Competitions',
      value: stats?.totalCompetitions || 0,
      change: 12,
      iconBgColor: 'bg-primary-100',
    },
    {
      icon: <Award className="w-6 h-6 text-green-600" />,
      label: 'Active Competitions',
      value: stats?.activeCompetitions || 0,
      iconBgColor: 'bg-green-100',
    },
    {
      icon: <Users className="w-6 h-6 text-blue-600" />,
      label: 'Total Submissions',
      value: stats?.totalSubmissions || 0,
      iconBgColor: 'bg-blue-100',
    },
    {
      icon: <Clock className="w-6 h-6 text-orange-600" />,
      label: 'Pending Reviews',
      value: stats?.pendingSubmissions || 0,
      iconBgColor: 'bg-orange-100',
    },
  ]

  return (
    <GovLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
                Competition Management
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                Create and manage TB awareness competitions
              </p>
            </div>
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => {
                setFormData(initialFormData)
                setIsCreateModalOpen(true)
              }}
            >
              Create Competition
            </Button>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          {statsCards.map((stat, index) => (
            <StatsCard
              key={index}
              icon={stat.icon}
              label={stat.label}
              value={stat.value}
              iconBgColor={stat.iconBgColor}
            />
          ))}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex gap-3 mb-6"
        >
          <Button
            variant="outline"
            leftIcon={<BarChart2 className="w-4 h-4" />}
            onClick={() => navigate('/admin/competitions/analytics')}
          >
            View Analytics
          </Button>
          <Button
            variant="outline"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={() => exportToCSV('competitions', competitionExportColumns, filteredCompetitions)}
            disabled={!filteredCompetitions.length}
          >
            Export CSV
          </Button>
          <Button
            variant="outline"
            leftIcon={<FileText className="w-4 h-4" />}
            onClick={() => exportToPDF('competitions', 'Competitions Report', competitionExportColumns, filteredCompetitions)}
            disabled={!filteredCompetitions.length}
          >
            Export PDF
          </Button>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6 relative z-50"
        >
          <Card className="p-4 overflow-visible">
            <div className="flex flex-col md:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1">
                <InputField
                  placeholder="Search competitions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<Search className="w-4 h-4" />}
                  className="w-full"
                />
              </form>
              <div className="flex gap-3">
                <Select
                  options={[{ value: '', label: 'All Types' }, ...COMPETITION_TYPES.map(t => ({ value: t.value, label: t.label }))]}
                  value={filterType}
                  onChange={setFilterType}
                  placeholder="Type"
                  className="w-40"
                />
                <Select
                  options={[{ value: '', label: 'All Status' }, ...COMPETITION_STATUS.map(s => ({ value: s.value, label: s.label }))]}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  placeholder="Status"
                  className="w-40"
                />
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <Tabs
            tabs={[
              { id: 'all', label: 'All', count: competitions.length },
              { id: 'draft', label: 'Draft', count: competitions.filter(c => c.status === 'DRAFT').length },
              { id: 'active', label: 'Active', count: competitions.filter(c => c.status === 'ACTIVE').length },
              { id: 'closed', label: 'Closed', count: competitions.filter(c => c.status === 'CLOSED').length },
            ]}
            activeTab={activeTab}
            onChange={setActiveTab}
            variant="default"
          />
        </motion.div>

        {/* Competitions Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-20 bg-neutral-200 dark:bg-neutral-800 rounded-lg" />
                </div>
              ))}
            </div>
          ) : filteredCompetitions.length === 0 ? (
            <EmptyState
              icon={<Trophy className="w-16 h-16 text-neutral-300" />}
              title="No competitions found"
              description="Create your first competition to get started"
              action={
                <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
                  Create Competition
                </Button>
              }
            />
          ) : (
            <div className="space-y-4">
              {filteredCompetitions.map((competition) => {
                const TypeIcon = getTypeIcon(competition.type)
                return (
                  <Card key={competition.id} hover="lift" className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl ${getTypeColor(competition.type)}`}>
                          <TypeIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-neutral-900 dark:text-white">
                              {competition.title}
                            </h3>
                            <Badge
                              variant={
                                competition.status === 'ACTIVE' ? 'success' :
                                competition.status === 'DRAFT' ? 'default' :
                                competition.status === 'CLOSED' ? 'danger' : 'default'
                              }
                              size="sm"
                            >
                              {competition.statusDisplayName || competition.status}
                            </Badge>
                            {competition.isFeatured && (
                              <Badge variant="warning" size="sm">Featured</Badge>
                            )}
                          </div>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2 mb-2">
                            {competition.description}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-neutral-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {competition.startDate ? new Date(competition.startDate).toLocaleDateString() : 'TBD'} - {competition.endDate ? new Date(competition.endDate).toLocaleDateString() : 'TBD'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              {competition.currentParticipants || 0} / {competition.maxParticipants || '∞'}
                            </span>
                            <Badge variant="outline" size="sm">
                              {competition.typeDisplayName || competition.type}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {competition.status === 'DRAFT' && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handlePublish(competition)}
                          >
                            Publish
                          </Button>
                        )}
                        {competition.status === 'ACTIVE' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleClose(competition)}
                          >
                            Close
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => navigate(`/admin/competitions/${competition.id}/submissions`)}
                          title="View Submissions"
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleExportSubmissions(competition)}
                          title="Export Submissions"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => navigate(`/admin/competitions/${competition.id}/analytics`)}
                          title="View Analytics"
                        >
                          <BarChart2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() =>
                            navigate(`/competitions/${competition.id}`, {
                              state: { from: '/admin/competitions' },
                            })
                          }
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => openEditModal(competition)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => {
                            setSelectedCompetition(competition)
                            setIsDeleteModalOpen(true)
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>
          )}
        </motion.div>

        {/* Create Modal */}
        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          title="Create Competition"
          description="Create a new TB awareness competition"
          size="xl"
        >
          <CompetitionForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreate}
            submitting={submitting}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Competition"
          description="Update competition details"
          size="xl"
        >
          <CompetitionForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleUpdate}
            submitting={submitting}
            onCancel={() => setIsEditModalOpen(false)}
          />
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Competition"
          size="sm"
        >
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Delete Competition?
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
              Are you sure you want to delete "{selectedCompetition?.title}"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handleDelete}
                loading={submitting}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </GovLayout>
  )
}

// Competition Form Component
interface CompetitionFormProps {
  formData: CompetitionFormData
  setFormData: React.Dispatch<React.SetStateAction<CompetitionFormData>>
  onSubmit: () => void
  submitting: boolean
  onCancel: () => void
}

const CompetitionForm: React.FC<CompetitionFormProps> = ({
  formData,
  setFormData,
  onSubmit,
  submitting,
  onCancel,
}) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Title"
          placeholder="Enter competition title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
        <Select
          label="Competition Type"
          options={COMPETITION_TYPES.map(t => ({ value: t.value, label: t.label }))}
          value={formData.type}
          onChange={(value) => setFormData({ ...formData, type: value as CompetitionType })}
        />
      </div>

      <InputField
        label="Description"
        placeholder="Enter competition description"
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Start Date"
          type="date"
          value={formData.startDate}
          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
        />
        <InputField
          label="End Date"
          type="date"
          value={formData.endDate}
          onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Max Participants"
          type="number"
          placeholder="Leave empty for unlimited"
          value={formData.maxParticipants || ''}
          onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) || 0 })}
        />
        <InputField
          label="Prize Description"
          placeholder="e.g., First Prize: ₹10,000"
          value={formData.prizeDescription}
          onChange={(e) => setFormData({ ...formData, prizeDescription: e.target.value })}
        />
      </div>

      <InputField
        label="Rules"
        placeholder="Enter competition rules (one per line)"
        value={formData.rules}
        onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
      />

      <InputField
        label="Guidelines"
        placeholder="Enter submission guidelines"
        value={formData.guidelines}
        onChange={(e) => setFormData({ ...formData, guidelines: e.target.value })}
      />

      <div className="flex gap-3 pt-4">
        <Button variant="outline" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" className="flex-1" onClick={onSubmit} loading={submitting}>
          {submitting ? 'Saving...' : 'Save Competition'}
        </Button>
      </div>
    </div>
  )
}

export default AdminCompetitionsPage